import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import ListHeader from '../components/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import ITable, { AvailableColumn } from '../uikit/itable/ITable';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useUserSorts } from '../hooks/useUserSorts';
import { useGetAllEmployees, UseGetAllEmployeesParams, EmployeeAvailableColumns, EmployeeSortParam } from '../services/react-query/employees';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { Employee } from '../types/Employee';
import { UserConfigurableModule } from '../types/User';
import { Loading } from '../uikit';
import { EmployeeColumns, EmployeeFilters } from '../utility/employee';
import { listPageLayoutStyles, listPageTableWrapperStyles } from '../styles/sxStyling';
import { useIsMobile } from '../hooks/useIsMobile';
import PreferencePill from '../components/preferences/PreferencePill';
import { RolePillsCell } from '../components/preferences/RolePillsCell';
import { FilterState } from '../components/common/filters/interfaces';
import _ from 'lodash';
import { useDownloadCsv } from '../hooks/useDownloadCsv';

const EmployeesList: React.FC = () => {
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const { userSorts } = useUserSorts<EmployeeSortParam>(UserConfigurableModule.EMPLOYEES);

  const { isMobile } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const { data: preferenceData } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.EMPLOYEE_ROLE, PREFERENCES_VALID_KEYS.EMPLOYEE_TITLE] } },
  });
  const prefByUuidObj = useMemo(() => _.keyBy(preferenceData ?? [], 'uuid'), [preferenceData]);

  const baseParams = {
    search: useSearchTerm(),
    filters: filterState,
    sort: userSorts,
  };

  const queryParams: UseGetAllEmployeesParams = {
    ...baseParams,
    page: currentPage,
  };

  const { data, isLoading: loading } = useGetAllEmployees({ params: queryParams, enabled: true });

  const pillColumns: AvailableColumn<Employee, EmployeeAvailableColumns>[] = useMemo(() => {
    return EmployeeColumns.map((col) => {
      if (col.key === 'title_value') {
        return {
          ...col,
          render: (row: Employee) => {
            const pref = row.title_uuid ? prefByUuidObj[row.title_uuid] : undefined;
            return pref ? <PreferencePill {...pref} /> : <PreferencePill value={row.title_value ?? ''} color={null} />;
          },
        };
      }

      if (col.key === 'roles') {
        return {
          ...col,
          sortable: false,
          render: (row: Employee) => {
            const rolesUuids = _.isArray(row.roles_uuids) ? row.roles_uuids : [];
            return <RolePillsCell rolesUuids={rolesUuids} prefByUuid={prefByUuidObj} />;
          },
        };
      }
      return col;
    }) as AvailableColumn<Employee, EmployeeAvailableColumns>[];
  }, [prefByUuidObj]);

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: 'employees',
  });

  if (loading) return <Loading />;

  const employees = data?.data || [];
  const meta = data?.meta;

  return (
    <Box>
      <ListHeader
        title="Employees"
        filtersOpen={sidebarOpen}
        onToggleFilters={() => setSidebarOpen((o) => !o)}
      />

      <Box sx={listPageLayoutStyles}>
        {sidebarOpen && (
          <FilterSidebar
            moduleFilters={EmployeeFilters(preferenceData)}
            module={UserConfigurableModule.EMPLOYEES}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />
        )}

        <Box sx={listPageTableWrapperStyles}>
          <ITable<Employee, EmployeeAvailableColumns>
            availableColumns={pillColumns}
            data={employees}
            module={UserConfigurableModule.EMPLOYEES}
            page={currentPage}
            count={meta?.total_pages ?? 1}
            isDownloadingCsv={isDownloadingCsv}
            onDownloadCsv={handleDownloadCsv}
            onPageChange={(_e, v) => setCurrentPage(v)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EmployeesList;
